// Team-work badges: count items added by anyone (typically a teammate)
// that THIS user hasn't seen yet.
//
// Storage (all in localStorage):
//
//   `deskte:lastSeenTab:${userId}:${clientId}` → JSON
//     `{ policies: ISO, docs: ISO, signatures: ISO, tasks: ISO,
//        eligibility: ISO, history: ISO, activity: ISO }`
//     Each tab gets stamped to NOW when the user clicks that tab on the
//     client profile. Badge for tab T = items with timestamp > stamp[T].
//
//   `deskte:lastVisited:${userId}:${clientId}` → ISO
//     One timestamp per (user, client), updated on unmount. Drives the
//     coarse "any new since you left" badge on the clients list.
//
// First visit ever (no stamps for this user+client yet):
//   * Tab stamps are seeded with mount-time so items added DURING the
//     visit count (e.g., agent uploads an eligibility PDF, switches to
//     another tab → eligibility shows [1]).
//   * The per-client `lastVisited` is NOT seeded; first visit shows no
//     badge in the clients list (we don't know what's "new" if you've
//     never been there).
//
// 2026-06-14: now server-synced. Reads/writes go through uiPrefsStore, which
// mirrors them into the user's uiPrefs (server) so the "unread" baseline follows
// the user across devices instead of being single-device.
import { prefsGet, prefsSet } from './uiPrefsStore.js';

const STORAGE_PREFIX = 'deskte:lastVisited';
const TAB_STORAGE_PREFIX = 'deskte:lastSeenTab';

// Tabs that we count items for. Keep in sync with ClientDetail's
// `tabBadges` shape. Lissette 2026-05-27: also track notes + comms so
// any new add/change anywhere in the profile surfaces a pelotica.
export const TRACKABLE_TABS = [
  'policies', 'docs', 'signatures', 'tasks', 'eligibility', 'history', 'activity', 'notes', 'lifechange', 'comms',
];

function tabKey(userId, clientId) {
  const u = userId || 'anon';
  const c = clientId || 'unknown';
  return `${TAB_STORAGE_PREFIX}:${u}:${c}`;
}

// Read the per-tab lastSeen map for one (user, client) pair.
// Returns {} when no entry exists (caller treats that as "first visit").
export function getLastSeenTabs(userId, clientId) {
  if (typeof window === 'undefined') return {};
  try {
    const raw = prefsGet(tabKey(userId, clientId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch {
    return {};
  }
}

export function setLastSeenTabs(userId, clientId, map) {
  if (typeof window === 'undefined') return;
  if (!clientId) return;
  try {
    prefsSet(tabKey(userId, clientId), JSON.stringify(map || {}));
  } catch {
    /* quota — silent */
  }
}

function storageKey(userId, clientId) {
  const u = userId || 'anon';
  const c = clientId || 'unknown';
  return `${STORAGE_PREFIX}:${u}:${c}`;
}

// Read the last-visited timestamp for one (user, client) pair.
// Returns an ISO string or null when no entry exists.
export function getLastVisited(userId, clientId) {
  if (typeof window === 'undefined') return null;
  try {
    return prefsGet(storageKey(userId, clientId));
  } catch {
    return null;
  }
}

// Write the timestamp. Call this on PROFILE UNMOUNT so the user's
// next visit shows items added between the two visits.
export function markClientVisited(userId, clientId, when = new Date()) {
  if (typeof window === 'undefined') return;
  if (!clientId) return;
  try {
    const iso = when instanceof Date ? when.toISOString() : String(when);
    prefsSet(storageKey(userId, clientId), iso);
  } catch {
    /* quota / private-mode — silent */
  }
}

// Count items whose timestamp is newer than the last-visited cutoff.
// Returns 0 when there's no cutoff yet (first visit ever — we don't
// know what's "new" so we don't surface badges yet; the unmount on
// this visit establishes the baseline for next time).
export function countNewerThan(items, tsField, sinceIso) {
  if (!sinceIso) return 0;
  if (!Array.isArray(items) || items.length === 0) return 0;
  const sinceMs = new Date(sinceIso).getTime();
  if (Number.isNaN(sinceMs)) return 0;
  let n = 0;
  for (const it of items) {
    const v = it && it[tsField];
    if (!v) continue;
    const itMs = new Date(v).getTime();
    if (!Number.isNaN(itMs) && itMs > sinceMs) n++;
  }
  return n;
}

// Total count across every trackable tab for one client. Used by the
// clients-list badge next to the client name.
//
// Lissette 2026-05-27 redesign: uses the SAME per-tab `lastSeenTabs`
// stamps the profile uses, so additions during a visit count as soon
// as the user leaves the profile (NOT after a second visit, which was
// the bug in PR #365). For each trackable tab, count items added
// after THAT tab's stamp. Sum across tabs.
//
// For clients the user has never opened: returns 0 (no baseline yet —
// we don't want every client to look "new" with N=∞ on first signin).
export function totalNewForClient(args) {
  const {
    userId,
    clientId,
    policies = [],
    docs = [],
    signatures = [],
    tasks = [],
    eligibilityAlerts = [],
    changeHistory = [],
    activitylog = [],
    clientNotes = [],
  } = args || {};
  if (!clientId) return 0;
  const stamps = getLastSeenTabs(userId, clientId);
  if (Object.keys(stamps).length === 0) return 0; // never visited yet
  const sameClient = (row, key = 'clientId') => row && row[key] === clientId;
  const myNotes = clientNotes.filter((n) => sameClient(n));
  return (
    countNewerThan(policies.filter((p) => sameClient(p)), 'createdAt', stamps.policies) +
    countNewerThan(docs.filter((d) => sameClient(d)), 'uploadedAt', stamps.docs) +
    countNewerThan(signatures.filter((s) => sameClient(s)), 'signedAt', stamps.signatures) +
    countNewerThan(tasks.filter((t) => sameClient(t)), 'createdAt', stamps.tasks) +
    countNewerThan(eligibilityAlerts.filter((e) => sameClient(e)), 'createdAt', stamps.eligibility) +
    countNewerThan(changeHistory.filter((h) => sameClient(h)), 'date', stamps.history) +
    // Regular notes vs "Cambio de vida" notes count against their own stamps.
    countNewerThan(myNotes.filter((n) => n.category !== 'life_change'), 'createdAt', stamps.notes) +
    countNewerThan(myNotes.filter((n) => n.category === 'life_change'), 'createdAt', stamps.lifechange) +
    countNewerThan(
      activitylog.filter((a) => a && (a.entityId === clientId || a.clientId === clientId)),
      'createdAt',
      stamps.activity,
    )
  );
}
